import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

/**
 * Attribution seam for the staff-PIN quick-switch (features/roles-and-permissions.md R5/R8). The
 * shell OWNS the mount point + this context; the PIN entry dialog, device sessions and lockouts land
 * in roles-and-permissions and call [ActingStaffContext.setActingStaff] after a successful
 * `POST /device-sessions/{id}/switch`. Attribution rides this context, never component props, so a
 * later surface reads "who is acting" without threading it through every screen. The AUTHORITATIVE
 * attribution of sensitive actions stays a backend concern — this only drives the header badge.
 */
export interface ActingStaff {
  id: string;
  displayName: string;
  role?: string;
}

interface ActingStaffContextValue {
  actingStaff: ActingStaff | null;
  setActingStaff: (staff: ActingStaff | null) => void;
}

const ActingStaffCtx = createContext<ActingStaffContextValue | null>(null);

export function ActingStaffProvider({ children }: { children: ReactNode }) {
  const [actingStaff, setActingStaff] = useState<ActingStaff | null>(null);
  const value = useMemo(() => ({ actingStaff, setActingStaff }), [actingStaff]);
  return <ActingStaffCtx.Provider value={value}>{children}</ActingStaffCtx.Provider>;
}

/** Read/set the acting staff. Throws outside the shell (a programming error). */
export function useActingStaff(): ActingStaffContextValue {
  const ctx = useContext(ActingStaffCtx);
  if (!ctx) throw new Error("useActingStaff() must be used inside an ActingStaffProvider");
  return ctx;
}
